import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Clock, MapPin, Building2, CheckCircle2, Loader2, ShieldCheck, CalendarPlus, Navigation, Phone, HeartPulse, UserRound, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

const BLOOD = ["", "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "No sé"];
const empty = { patient_name: "", sex: "", birth_date: "", curp: "", guardian_name: "", guardian_relationship: "", phone: "", email: "", blood_type: "", allergies: "", medications: "", conditions: "", reason: "", address: "", consent: false };

const Field = ({ label, children, hint }) => (
  <div>
    <Label className="text-[11px] font-bold uppercase tracking-wider text-brand-ink/60">{label}</Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const ApptCard = ({ info }) => (
  <div data-testid="intake-appt-card" className="rounded-3xl bg-[#0D47A1] text-white p-5 sm:p-6 relative overflow-hidden">
    <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#00B4CB]/30" />
    <div className="absolute right-6 -bottom-12 w-28 h-28 rounded-full bg-white/10" />
    <p className="text-xs font-bold uppercase tracking-widest text-sky-200 relative">Tu cita</p>
    <p className="font-outfit font-bold text-xl sm:text-2xl mt-1 relative">{info.doctor_name}</p>
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 mt-4 text-sm relative">
      <span className="flex items-center gap-2"><CalendarDays size={16} className="text-sky-300" /> {info.fecha}</span>
      <span className="flex items-center gap-2"><Clock size={16} className="text-sky-300" /> {info.hora}</span>
      {info.clinic_name && <span className="flex items-center gap-2"><Building2 size={16} className="text-sky-300" /> {info.clinic_name}</span>}
      {info.address && <span className="flex items-start gap-2"><MapPin size={16} className="text-sky-300 mt-0.5 shrink-0" /> {info.address}</span>}
    </div>
  </div>
);

const QuickLinks = ({ info }) => (
  <div className="flex flex-wrap gap-2.5">
    <a data-testid="intake-add-calendar" href={info.calendar_url} target="_blank" rel="noopener noreferrer"
      className="flex items-center gap-2 bg-white border border-stone-200 hover:border-[#0D47A1] text-brand-ink text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
      <CalendarPlus size={16} className="text-[#0D47A1]" /> Agregar a mi calendario</a>
    {info.maps_url && (
      <a data-testid="intake-directions" href={info.maps_url} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 bg-white border border-stone-200 hover:border-[#0D47A1] text-brand-ink text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
        <Navigation size={16} className="text-[#00B4CB]" /> Cómo llegar</a>
    )}
    {info.phone && (
      <a data-testid="intake-call" href={`tel:${info.phone}`}
        className="flex items-center gap-2 bg-white border border-stone-200 hover:border-[#0D47A1] text-brand-ink text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
        <Phone size={16} className="text-[#F59E0B]" /> {info.phone}</a>
    )}
  </div>
);

const Done = ({ info }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="intake-done"
    className="bg-white rounded-3xl border border-stone-100 p-7 text-center shadow-[0_10px_40px_rgb(13,71,161,0.08)]">
    <img src="/oci/happy.png" alt="Oci" className="w-28 h-28 mx-auto rounded-full object-cover bg-white" />
    <h2 className="font-outfit font-bold text-2xl text-brand-ink mt-3 flex items-center justify-center gap-2"><CheckCircle2 className="text-emerald-500" /> ¡Registro completo!</h2>
    <p className="text-muted-foreground mt-2 max-w-sm mx-auto">Gracias. {info.doctor_name} ya tiene tus datos para preparar tu atención. Te enviaremos un recordatorio un día antes.</p>
    <div className="flex justify-center mt-5"><QuickLinks info={info} /></div>
  </motion.div>
);

const yearsFrom = (iso) => { const b = new Date(iso); if (isNaN(b)) return null; return (Date.now() - b.getTime()) / 31557600000; };

const Identity = ({ f, set, setF }) => (
  <>
    <div>
      <h2 className="font-outfit font-bold text-lg text-brand-ink flex items-center gap-2"><UserRound size={18} className="text-[#0D47A1]" /> Ficha de identificación</h2>
      <p className="text-xs text-muted-foreground mt-0.5">Es tu primera consulta: con estos datos tu médico abrirá tu expediente.</p>
    </div>
    <Field label="Nombre completo del paciente"><Input data-testid="intake-patient-name" value={f.patient_name} onChange={set("patient_name")} className="rounded-xl" placeholder="Nombre(s) y apellidos" /></Field>
    <div className="grid sm:grid-cols-3 gap-4">
      <Field label="Sexo">
        <div className="flex bg-muted rounded-xl p-1">
          {[["M", "Masculino"], ["F", "Femenino"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`intake-sex-${v}`} onClick={() => { setF({ ...f, sex: v }); }}
              className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-colors ${f.sex === v ? "bg-[#0D47A1] text-white" : "text-brand-ink/60"}`}>{l}</button>
          ))}
        </div>
      </Field>
      <Field label="Fecha de nacimiento"><Input data-testid="intake-birth-date" type="date" value={f.birth_date} onChange={set("birth_date")} className="rounded-xl" /></Field>
      <Field label="CURP (opcional)"><Input data-testid="intake-curp" value={f.curp} onChange={set("curp")} className="rounded-xl uppercase" maxLength={18} /></Field>
    </div>
    <Field label="Domicilio"><Input data-testid="intake-address" value={f.address} onChange={set("address")} className="rounded-xl" placeholder="Calle, número, colonia, ciudad" /></Field>
    <div className="pt-1 border-t border-stone-100" />
  </>
);

function IntakeForm({ info, onDone }) {
  const [f, setF] = useState({ ...empty, patient_name: info.patient_name || "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (info.is_new && (!f.patient_name.trim() || !f.sex || !f.birth_date)) { setErr("Completa nombre, sexo y fecha de nacimiento del paciente."); return; }
    if (!f.consent) { setErr("Para continuar acepta el aviso de privacidad."); return; }
    setSaving(true); setErr("");
    try { await api.post(`/public/intake/${info.token}`, f); onDone(); }
    catch (ex) { setErr(ex?.response?.data?.detail || "No se pudo enviar. Intenta de nuevo."); }
    finally { setSaving(false); }
  };

  const yrs = info.is_new ? yearsFrom(f.birth_date) : null;
  const minor = info.is_new ? (yrs !== null && yrs < 18) : info.is_minor;
  return (
    <form onSubmit={submit} data-testid="intake-form" className="bg-white rounded-3xl border border-stone-100 p-5 sm:p-7 space-y-5 shadow-[0_10px_40px_rgb(13,71,161,0.06)]">
      {info.is_new && <Identity f={f} set={set} setF={setF} />}
      <div>
        <h2 className="font-outfit font-bold text-lg text-brand-ink">Datos de contacto</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{minor ? "Si la consulta es para un menor, la madre, padre o tutor puede llenarlo." : "Solo te tomará 2 minutos."}</p>
      </div>
      {minor && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nombre del tutor"><Input data-testid="intake-guardian-name" value={f.guardian_name} onChange={set("guardian_name")} className="rounded-xl" placeholder="Nombre completo" /></Field>
          <Field label="Parentesco"><Input data-testid="intake-guardian-rel" value={f.guardian_relationship} onChange={set("guardian_relationship")} className="rounded-xl" placeholder="Madre, padre, tutor…" /></Field>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Teléfono / WhatsApp"><Input data-testid="intake-phone" type="tel" value={f.phone} onChange={set("phone")} className="rounded-xl" placeholder="10 dígitos" /></Field>
        <Field label="Correo electrónico"><Input data-testid="intake-email" type="email" value={f.email} onChange={set("email")} className="rounded-xl" placeholder="tucorreo@ejemplo.com" /></Field>
      </div>
      <div className="pt-1 border-t border-stone-100" />
      <h2 className="font-outfit font-bold text-lg text-brand-ink flex items-center gap-2"><HeartPulse size={18} className="text-brand-terracotta" /> Salud de {(f.patient_name || info.patient_first_name || "").split(" ")[0] || "el paciente"}</h2>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Tipo de sangre">
          <select data-testid="intake-blood" value={f.blood_type} onChange={set("blood_type")} className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
            {BLOOD.map((b) => <option key={b} value={b}>{b || "Selecciona"}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2"><Field label="Alergias"><Input data-testid="intake-allergies" value={f.allergies} onChange={set("allergies")} className="rounded-xl" placeholder="Medicamentos, alimentos… o «Ninguna»" /></Field></div>
      </div>
      <Field label="Medicamentos que toma actualmente"><Input data-testid="intake-meds" value={f.medications} onChange={set("medications")} className="rounded-xl" placeholder="Nombre y dosis, si aplica" /></Field>
      <Field label="Enfermedades o condiciones previas"><Input data-testid="intake-conditions" value={f.conditions} onChange={set("conditions")} className="rounded-xl" placeholder="Asma, diabetes, cirugías…" /></Field>
      <Field label="¿Cuál es el motivo de la consulta?"><Textarea data-testid="intake-reason" value={f.reason} onChange={set("reason")} className="rounded-xl" rows={3} placeholder="Cuéntanos brevemente los síntomas o el motivo" /></Field>
      <label className="flex items-start gap-3 bg-[#F1F6FD] rounded-2xl p-3.5 cursor-pointer">
        <Checkbox data-testid="intake-consent" checked={f.consent} onCheckedChange={(v) => { setF({ ...f, consent: !!v }); if (v) setErr(""); }} className="mt-0.5" />
        <span className="text-xs text-brand-ink/80 leading-relaxed">Acepto que mis datos se compartan con mi médico para preparar mi atención, conforme al <a href="/privacidad" target="_blank" className="font-bold text-[#0D47A1] underline">aviso de privacidad</a>.</span>
      </label>
      {err && <p data-testid="intake-error" className="text-sm text-red-600 font-medium">{err}</p>}
      <button data-testid="intake-submit" type="submit" disabled={saving}
        className="w-full flex items-center justify-center gap-2 bg-[#0D47A1] hover:bg-[#0B3C8A] active:scale-[0.99] text-white font-outfit font-semibold py-3.5 rounded-2xl transition-[background-color,transform] disabled:opacity-60">
        {saving ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />} Enviar mi registro
      </button>
    </form>
  );
}

function Attendance({ info, action, onChange }) {
  const [busy, setBusy] = useState(false);
  const [askCancel, setAskCancel] = useState(action === "cancelar");
  const [msg, setMsg] = useState("");
  const call = async (what) => {
    setBusy(true); setMsg("");
    try { await api.post(`/public/appointments/${info.token}/${what}`); onChange(what); setAskCancel(false); }
    catch (ex) { setMsg(ex?.response?.data?.detail || "No pudimos registrar tu respuesta. Intenta de nuevo o llama a tu consultorio."); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (action === "confirmar" && !info.attendance_confirmed) call("confirm"); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (askCancel) return (
    <div data-testid="attend-cancel-ask" className="bg-white rounded-3xl border-2 border-brand-ink p-5 space-y-3">
      <p className="font-outfit font-bold text-brand-ink">¿Seguro que quieres cancelar tu cita del {info.fecha} a las {info.hora}?</p>
      <p className="text-xs text-muted-foreground">Le avisaremos a tu médico y el horario quedará libre.</p>
      <div className="flex gap-2.5">
        <button data-testid="attend-cancel-no" onClick={() => setAskCancel(false)} className="flex-1 py-3 rounded-2xl text-sm font-semibold bg-stone-100 text-brand-ink">No, conservarla</button>
        <button data-testid="attend-cancel-yes" disabled={busy} onClick={() => call("cancel")} className="flex-1 py-3 rounded-2xl text-sm font-semibold bg-brand-ink text-white disabled:opacity-60">{busy ? "Cancelando…" : "Sí, cancelar"}</button>
      </div>
      {msg && <p data-testid="attend-error" className="text-sm text-red-600">{msg}</p>}
    </div>
  );
  return (
    <div data-testid="attend-box" className="bg-white rounded-3xl border border-stone-100 p-5 flex flex-wrap items-center justify-between gap-3">
      {info.attendance_confirmed ? (
        <p data-testid="attend-confirmed" className="font-outfit font-bold text-brand-ink flex items-center gap-2"><CheckCircle2 className="text-[#0D47A1]" size={20} /> Asistencia confirmada. ¡Te esperamos!</p>
      ) : (
        <>
          <p className="font-outfit font-bold text-brand-ink">¿Asistirás a tu cita?</p>
          <button data-testid="attend-confirm-btn" disabled={busy} onClick={() => call("confirm")}
            className="flex items-center gap-1.5 bg-[#0D47A1] hover:bg-[#0B3C8A] text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors disabled:opacity-60"><CheckCircle2 size={16} /> Confirmar asistencia</button>
        </>
      )}
      <button data-testid="attend-cancel-btn" onClick={() => setAskCancel(true)} className="flex items-center gap-1.5 text-sm font-semibold text-brand-ink/70 hover:text-brand-ink transition-colors"><XCircle size={16} /> Cancelar mi cita</button>
      {msg && <p data-testid="attend-error" className="w-full text-sm text-red-600">{msg}</p>}
    </div>
  );
}

export default function PreRegistro() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const action = params.get("accion");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onAttend = (what) => setInfo((i) => what === "cancel"
    ? { ...i, status: "cancelada", cancelled_by_patient: true } : { ...i, attendance_confirmed: true });

  useEffect(() => {
    api.get(`/public/intake/${token}`)
      .then((r) => { setInfo({ ...r.data, token }); setDone(r.data.completed); })
      .catch((e) => setError(e?.response?.data?.detail || "No pudimos cargar tu cita."));
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="intake-page">
      <header className="max-w-2xl mx-auto px-5 pt-6 pb-2 flex items-center justify-between"><Logo size={38} /></header>
      <main className="max-w-2xl mx-auto px-5 pb-16 pt-4 space-y-5">
        {error && <div data-testid="intake-load-error" className="bg-white rounded-3xl p-8 text-center text-brand-ink border border-stone-100">{error}</div>}
        {!info && !error && <div className="h-60 flex items-center justify-center"><Loader2 className="animate-spin text-[#0D47A1]" /></div>}
        {info && (
          <AnimatePresence mode="wait">
            <motion.div key={done ? "d" : "f"} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <div className="flex items-end gap-4">
                <img src="/oci/wave.png" alt="Oci" className="w-20 h-20 rounded-full object-cover bg-white border-4 border-white shadow-md" />
                <div className="pb-1">
                  <h1 className="font-outfit font-extrabold text-3xl sm:text-4xl text-brand-ink tracking-tight">¡Hola!</h1>
                  <p className="text-sm text-muted-foreground">{info.status !== "programada" ? "Tu cita ya no está activa." : done ? "Tu cita está lista." : `Completa el pre-registro de ${info.patient_first_name} antes de la visita.`}</p>
                </div>
              </div>
              <ApptCard info={info} />
              {info.status !== "programada" ? (
                <div data-testid="intake-inactive" className="bg-white rounded-3xl p-6 text-center border border-stone-100">
                  {info.cancelled_by_patient ? "Tu cita fue cancelada. Le avisamos a tu médico. Si deseas reagendar, comunícate con tu consultorio." : "Esta cita ya no está activa. Comunícate con tu consultorio."}
                </div>
              ) : done ? (
                <>
                  <Attendance info={info} action={action} onChange={onAttend} />
                  <Done info={info} />
                </>
              ) : (
                <>
                  <Attendance info={info} action={action} onChange={onAttend} />
                  <QuickLinks info={info} />
                  <IntakeForm info={info} onDone={() => setDone(true)} />
                </>
              )}
            </motion.div>
          </AnimatePresence>
        )}
        <p className="text-center text-xs font-semibold text-[#0D47A1]/70 pt-4">ClinPed · Cerca de ti desde antes de tu consulta</p>
      </main>
    </div>
  );
}
